/**
 * Routing Types
 *
 * Shared types for route calculation across frontend and backend.
 * These types are used by:
 * - Frontend: day planning, my-day panels, workorders map
 * - Backend: routing controller, routing service
 */

/**
 * Route calculation result from API (successful route)
 */
export interface RouteResult {
  from: string;
  to: string;
  distance_km: number | null;
  duration_minutes: number | null;
  /** Array of [lng, lat] waypoints for drawing the route polyline */
  geometry: [number, number][];
  /** Whether this result was served from cache */
  cached: boolean;
  /** Optional warning message (route succeeded but with caveats) */
  error?: string;
}

/**
 * Route failure result from API
 * Includes from/to addresses for easy matching to workorders
 */
export interface RouteError {
  from: string;
  to: string;
  error: string;
}

/**
 * Union type for route API response items.
 * API returns either a successful RouteResult or a RouteError.
 */
export type RouteResponse = RouteResult | RouteError;

/**
 * Type guard to check if a route response is a successful route.
 * Uses 'distance_km' as the discriminating property.
 */
export function isRouteResult(
  route: RouteResponse | null,
): route is RouteResult {
  return route !== null && 'distance_km' in route;
}

/**
 * Type guard to check if a route response is an error.
 * Must have 'error' but not 'distance_km' to be a RouteError.
 */
export function isRouteError(route: RouteResponse | null): route is RouteError {
  return route !== null && 'error' in route && !('distance_km' in route);
}

/**
 * Result from geocoding an address
 */
export interface GeocodeResult {
  /** Original address that was geocoded */
  address: string;
  /** [lng, lat] coordinates or null if geocoding failed */
  coordinates: [number, number] | null;
  /** Error message if geocoding failed */
  error?: string;
}
